import { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { won, shortWon } from './tabData';
import { DayReportDto } from '../../types/opsBriefing';
import { useOpsContext } from './useOpsContext';
import {
    useOpsHistoryQuery,
    useSetHistoryItemDoneMutation,
} from '../../services/useOpsBriefingService';
import { OpsLoading, OpsError, OpsEmpty, OpsMockBadge, OpsComingSoonButton } from './OpsStates';
/*
 * 기록 상태(브리핑이 만들어졌나)는 건수(몇 건이었나)와 다른 축이다.
 * 둘을 한 파일에서 섞으면 "문서가 없는 날"이 다시 0 건인 날로 접힌다.
 */
import {
    cellKindOf,
    stateByDateOf,
    summarizeCoverage,
    legendKindsOf,
    CELL_META,
} from './coverage';
import { isOpsBriefingMock } from '../../api/opsBriefing';
import {
    LEVEL_META,
    levelOf,
    compareMonth,
    weeklyStats,
    weekdayStats,
    weekdayInsight,
    streakOf,
    recentTrend,
    dayLeftOut,
    summarizeComposition,
    defaultHistoryDate,
} from './historyAnalysis';
/*
 * 편성 문구(`briefingPolicy.explainComposition`)는 **이 화면이 더 이상 안 부른다**
 * (2026-09-01 QA33 — 노란 안내 두 덩이를 화면에서 뺐다). 함수와 셈은 그대로이고,
 * 밀린 날 표시는 히트맵의 접힌 모서리로 남는다.
 */
/*
 * 날짜 상세의 항목 목록도 오늘의 브리핑과 **같은 편성 함수**로 다섯 건만 세운다.
 * 규칙을 각자 적으면 같은 날을 두 화면에서 볼 때 다섯 건이 갈린다.
 */
import { composeHighlights, BRIEFING_FOCUS_COUNT, toFocusDay } from './briefingComposition';

dayjs.locale('ko');

/**
 * 지난 리포트 — 경영 회고.
 *
 * 이전 버전은 달력에 색만 칠했다. 색이 진하면 그날 잘한 것이지만,
 * "이번 달이 지난달보다 나은가", "어느 요일에 자꾸 놓치는가"는 알 수 없었다.
 * 게다가 오늘이 6일이면 9~31일이 전부 빈칸이라 화면의 3분의 2가 죽었다.
 *
 * 그래서 달력 하나에 기대지 않고 네 층으로 나눈다.
 *   ① 이번 달 성적 — 한 줄 판정 + 연속 기록
 *   ② 히트맵 — 주차별 요약을 오른쪽에 붙여 흐름이 보이게
 *   ③ 요일 패턴 — 바로 고칠 수 있는 운영 문제
 *   ④ 주간 추세 — 나아지는 중인가
 */

const BORDER = '#E3EAF3';
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

const GRADE_DOT: Record<string, string> = {
    DANGER: '#FF3E4A',
    WARNING: '#F0A020',
    CHANCE: '#3D73DD',
    PRAISE: '#00A793',
};
const GRADE_LABEL: Record<string, string> = {
    DANGER: '위험',
    WARNING: '주의',
    CHANCE: '기회',
    PRAISE: '칭찬',
};

/*
 * 룩업 폴백.
 *
 * grade 는 서버 문자열이라 화면이 모르는 값이 올 수 있습니다.
 * 예전에는 배지에 `undefined` 가 그대로 찍히고 색은 `undefined1A` 라는
 * 잘못된 CSS 가 되어 배경이 통째로 사라졌습니다.
 * 모르는 등급은 모른다고 표시하고, 항목 자체는 계속 보여줍니다 —
 * 등급을 몰라도 "무엇을 놓쳤는가"는 원장에게 필요한 정보입니다.
 */
const GRADE_FALLBACK_COLOR = '#7C8794';
const gradeColorOf = (grade: string) => GRADE_DOT[grade] ?? GRADE_FALLBACK_COLOR;
const gradeLabelOf = (grade: string) => GRADE_LABEL[grade] ?? '기타';

const pct = (v: number) => Math.round(v * 100);

/**
 * 달 이동.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 컴포넌트로 뺐나 — 갇히는 화면이었습니다
 * ─────────────────────────────────────────────────────────────
 * 예전에는 `‹ ›` 가 히트맵 카드 머리글 **안에만** 있었습니다.
 * 그런데 로딩·에러·'기록 없음'은 히트맵보다 먼저 return 으로 빠져나가서,
 * 기록이 없는 달로 넘어가는 순간 달 이동 버튼이 통째로 사라졌습니다.
 * 이번 달로 돌아올 방법이 없어 새로고침하거나 탭을 옮겼다 와야 했습니다 —
 * 원장님 눈에는 "지난달 갔다가 오늘로 못 돌아온다"로 보입니다.
 *
 * 나머지 세 탭이 DateNavigator 를 네 분기 전부에 넣어 두는 것과 같은 규칙입니다.
 * 어떤 상태에서도 날짜를 바꿀 수 있어야 합니다.
 */
const MonthNav = ({
    cursor,
    canPrev,
    canNext,
    /** 이번 달로 돌아갈 수 있는가 — 이미 이번 달이면 버튼을 숨긴다 */
    canReturnToThisMonth,
    onMove,
    onReturnToThisMonth,
}: {
    cursor: dayjs.Dayjs;
    canPrev: boolean;
    canNext: boolean;
    canReturnToThisMonth: boolean;
    onMove: (delta: number) => void;
    onReturnToThisMonth: () => void;
}) => (
    <div className="flex items-center" style={{ gap: 6 }}>
        {/*
          '이번 달로' 는 한 번에 돌아오는 길입니다.
          `›` 를 여러 번 눌러 되짚게 하면 중간에 빈 달이 끼었을 때 또 막힙니다.
        */}
        {canReturnToThisMonth && (
            <button
                type="button"
                onClick={onReturnToThisMonth}
                className="rounded-[8px]"
                style={{
                    height: 32,
                    padding: '0 12px',
                    border: `1px solid ${BORDER}`,
                    background: '#FFFFFF',
                    color: '#2C4C8C',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                    marginRight: 2,
                }}
            >
                이번 달로
            </button>
        )}
        <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={!canPrev}
            aria-label="이전 달"
            style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: `1px solid ${BORDER}`,
                background: '#FFFFFF',
                color: canPrev ? '#5B6B7F' : '#D3DAE4',
                cursor: canPrev ? 'pointer' : 'not-allowed',
            }}
        >
            ‹
        </button>
        <span
            style={{
                fontSize: 15.5,
                fontWeight: 700,
                color: '#16202E',
                minWidth: 96,
                textAlign: 'center',
            }}
        >
            {cursor.format('YYYY년 M월')}
        </span>
        <button
            type="button"
            onClick={() => onMove(1)}
            disabled={!canNext}
            aria-label="다음 달"
            style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                border: `1px solid ${BORDER}`,
                background: '#FFFFFF',
                color: canNext ? '#5B6B7F' : '#D3DAE4',
                cursor: canNext ? 'pointer' : 'not-allowed',
            }}
        >
            ›
        </button>
    </div>
);

const OpsBriefingHistoryPage = () => {
    const [monthKey, setMonthKey] = useState(() => dayjs().format('YYYY-MM'));
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    /** 자동으로 지난달로 물러난 적이 있는가 — 무한 후퇴를 막는다 */
    const steppedBack = useRef(false);
    /**
     * 사용자가 달을 직접 옮겼는가.
     *
     * 자동 후퇴는 **처음 들어왔을 때 이번 달이 비어 있는 경우**만을 위한 장치입니다.
     * 이 깃발이 없던 동안에는 `‹` 로 직접 넘어간 빈 달에서도 후퇴가 발동해
     * 한 달을 더 밀어냈습니다. 8월 → (‹) 7월 → 자동으로 6월.
     * 원장님은 7월을 보러 갔는데 6월이 떠 있고, 되돌아올 버튼은 그 화면에
     * 없었습니다. 사용자가 키를 잡은 뒤로는 화면이 달을 바꾸지 않습니다.
     */
    const userMovedMonth = useRef(false);

    /** 자동으로 물러나기 전에 보고 있던 달 — 배너로 알리려면 기억해야 한다 */
    const [steppedBackFrom, setSteppedBackFrom] = useState<string | null>(null);

    /**
     * 들어오자마자 오늘을 한 번 열어 줬는가.
     *
     * **딱 한 번**이다. 원장님이 달을 옮기거나(goMonth 가 선택을 비운다)
     * 기록 없는 칸을 눌러 상세를 닫으면, 화면이 그걸 다시 열어젖히면 안 된다.
     * 사용자가 키를 잡은 뒤로 화면이 제멋대로 움직이지 않는다는 규칙은
     * 바로 위 `userMovedMonth` 와 같다.
     */
    const autoPickedDate = useRef(false);

    const ctx = useOpsContext();
    const { data, isLoading, isError, refetch } = useOpsHistoryQuery(ctx, monthKey, ctx.ready);
    const setItemDone = useSetHistoryItemDoneMutation(ctx, monthKey);

    /**
     * 오늘.
     *
     * ⚠️ 브라우저 시계로 정하지 않습니다. 브리핑 날짜는 전부 KST 이고,
     * 기기 시간대가 KST 가 아니면 '오늘' 배지와 미래 판정이 하루 어긋나
     * 아직 오지 않은 날에 "브리핑이 없다"고 쓰게 됩니다.
     * 서버가 `coverage.today` 로 자기가 본 오늘을 함께 주므로 그걸 씁니다.
     * (아직 응답 전이거나 구버전 서버면 그때만 기기 시계로 물러섭니다)
     */
    const today = useMemo(
        () => (data?.coverage?.today ? dayjs(data.coverage.today) : dayjs()).startOf('day'),
        [data]
    );

    /**
     * 그 달의 기록 상태.
     *
     * `days[]` 는 문서가 있는 날만 옵니다. 이것 없이는 화면이
     * "브리핑이 만들어지지 않은 날"과 "챙길 일이 0건이던 날"을 구분할 수 없습니다.
     */
    const stateByDate = useMemo(() => stateByDateOf(data?.coverage), [data]);
    const recordCoverage = useMemo(
        () => summarizeCoverage(data?.coverage, data?.days ?? [], monthKey),
        [data, monthKey]
    );

    /**
     * 전월 기록 — '지난달 대비' 판정에만 쓴다.
     *
     * ─────────────────────────────────────────────────────────────
     * 왜 쿼리를 하나 더 거는가
     * ─────────────────────────────────────────────────────────────
     * compareMonth 는 넘긴 배열 안에서 전월 키를 직접 찾습니다
     * (historyAnalysis.compareMonth). 그런데 API 는 month 하나만 받아
     * 한 달치만 내려주므로, 이 배열에는 전월이 **영영 들어 있지 않습니다.**
     * 그래서 판정이 언제나 NO_BASE('비교할 지난달 기록이 없습니다')였습니다 —
     * 기록이 멀쩡히 쌓인 센터에서도 그랬습니다.
     *
     * ⚠️ 이 응답은 비교에만 씁니다. 히트맵·요일 패턴·주간 추세·연속 기록에는
     *    섞지 마세요. 그쪽은 '이번 달' 화면이라, 전월이 섞이면 달력에 없는 날이
     *    요일 통계와 연속 기록에 끼어들어 숫자가 조용히 달라집니다.
     */
    const prevMonthKey = useMemo(
        () => dayjs(`${monthKey}-01`).subtract(1, 'month').format('YYYY-MM'),
        [monthKey]
    );
    const { data: prevData } = useOpsHistoryQuery(ctx, prevMonthKey, ctx.ready);

    /**
     * 서버가 준 그 달 기록.
     * 히트맵·요일 패턴·추세가 전부 이 배열에서 나온다.
     */
    /*
     * 🔴 목록과 **같은 모집단**으로 맞춘다 (2026-08-31 QA10).
     *
     * 지난 리포트는 그날의 다섯 건만 보여주는데 달력 점·「N건 중 M건 실행」은
     * 서버 총계를 쓰고 있었다 — 목록 5줄 / 달력 점 8개 / 「8건 중 0건 실행」.
     * 여기서 한 번 통과시키면 달력·주간 줄·월 실행률(summarizeMonth 가 이 값을
     * 합산한다)이 전부 같은 기준을 본다.
     */
    const reports = useMemo(() => (data?.days ?? []).map((d) => toFocusDay(d)), [data]);
    const effect = data?.effect;
    const insights = data?.insights;
    const [toast, setToast] = useState<string | null>(null);

    /*
     * 오늘이 1~2일이면 이번 달은 거의 비어 있다.
     * API 가 월 단위라 다른 달에 기록이 있는지는 불러봐야 안다.
     * 이번 달이 비었으면 딱 한 번만 지난달로 물러난다.
     *
     * 물러난 사실을 화면에 남긴다. 예전에는 조용히 바꿔서,
     * 8월 1일에 들어온 원장이 7월 숫자를 이번 달 실적으로 읽었다.
     */
    useEffect(() => {
        if (steppedBack.current || !data) return;
        // 사용자가 직접 옮긴 달에서는 물러나지 않는다 (userMovedMonth 주석 참고)
        if (userMovedMonth.current) return;
        /*
         * ⚠️ '올라온 항목이 있는 날'만으로 판정하면 안 됩니다.
         * 그러면 한 달 내내 브리핑이 만들어지지 않은 달도, 챙길 일이 0건이던 날만
         * 있던 달도 똑같이 접혀서 지난달로 물러납니다 — 둘 다 사실이 아니고,
         * 원장님은 "8월은 기록이 없구나"로 읽습니다.
         * 기록이 아예 없는 달(첫 기록 이전 등)일 때만 물러납니다.
         */
        if (recordCoverage.worthShowing) return;
        steppedBack.current = true;
        setSteppedBackFrom(monthKey);
        setMonthKey(dayjs(`${monthKey}-01`).subtract(1, 'month').format('YYYY-MM'));
    }, [data, monthKey, recordCoverage]);

    /*
     * ─────────────────────────────────────────────────────────────
     * 들어오면 오늘이 열려 있다
     * ─────────────────────────────────────────────────────────────
     * 예전에는 아무 날도 안 골라 둔 채 열려서, 히트맵에서 칸 하나를 찾아
     * 눌러야 그날 상세가 처음 나왔다. 매일 아침 여는 화면에서 "뭘 눌러야
     * 하는지"를 매번 찾게 만들면 대부분은 안 누르고 나간다.
     *
     * 오늘 기록이 없으면 가장 최근 기록일을 연다 (defaultHistoryDate).
     *
     * ⚠️ **자동 후퇴(위 useEffect)와 순서가 엮여 있다.**
     *    기록이 아예 없는 달에서는 곧 지난달로 물러나므로, 그 달에서 날짜를
     *    골라 봐야 다음 순간 달이 바뀌며 버려진다. 그래서 후퇴 판정과 **같은
     *    값**(recordCoverage.worthShowing)으로 문을 잠근다 — 물러날 달에서는
     *    고르지 않고, 물러난 뒤의 달에서 고른다. 판정이 어긋나면 안 되므로
     *    두 곳 다 이 값 하나만 본다.
     *
     * ⚠️ 고를 날이 없어도 깃발을 세운다. 안 그러면 기록이 없는 달을 보는 내내
     *    렌더마다 다시 시도하고, 원장님이 상세를 닫은 순간 도로 열린다.
     */
    useEffect(() => {
        if (autoPickedDate.current || !data) return;
        if (userMovedMonth.current) return;
        if (!recordCoverage.worthShowing) return;
        autoPickedDate.current = true;
        const pick = defaultHistoryDate(reports, today.format('YYYY-MM-DD'));
        if (pick) setSelectedDate(pick);
    }, [data, recordCoverage, reports, today]);

    const fire = (msg: string) => {
        setToast(msg);
        window.setTimeout(() => setToast(null), 2400);
    };



    const cursor = dayjs(`${monthKey}-01`);

    const byDate = useMemo(() => {
        const m: Record<string, DayReportDto> = {};
        reports.forEach((d) => {
            m[d.date] = d;
        });
        return m;
    }, [reports]);

    /*
     * 비교에만 전월을 합쳐 넘긴다 (위 prevData 주석 참고).
     * 전월 응답이 아직 안 왔거나 비어 있으면 지금처럼 NO_BASE 로 떨어지고,
     * 화면은 "비교할 지난달 기록이 없습니다"를 그대로 보여준다 — 그건 정직한 표시다.
     */
    const cmp = useMemo(
        () => compareMonth([...reports, ...(prevData?.days ?? [])], monthKey),
        [reports, prevData, monthKey]
    );
    const weeks = useMemo(() => weeklyStats(reports, monthKey), [reports, monthKey]);
    const wdStats = useMemo(() => weekdayStats(reports), [reports]);
    const wdInsight = useMemo(() => weekdayInsight(reports), [reports]);
    const streak = useMemo(() => streakOf(reports), [reports]);
    const trend = useMemo(() => recentTrend(reports, 8), [reports]);
    /*
     * 이 달 편성 기록 — 브리핑에 **오르지 못한** 후보가 있었나.
     *
     * 실행률은 올라온 항목만 센다. 자리에 밀려 애초에 안 올라온 건은
     * 이 화면 어디에도 안 남아서, 위험이 많았던 날의 재등록 기회가 통째로 사라진다.
     * 아직 오지 않은 날은 편성 기록이 없는 게 당연하므로 오늘까지만 센다.
     */
    const compCoverage = useMemo(
        () => summarizeComposition(reports, monthKey, today.format('YYYY-MM-DD')),
        [reports, monthKey, today]
    );

    /** 달력 격자 — 주 단위로 끊어 오른쪽에 주 요약을 붙인다 */
    const rows = useMemo(() => {
        const start = cursor.startOf('month');
        const pad = start.day();
        const days = cursor.daysInMonth();
        const cells: (string | null)[] = [];
        for (let i = 0; i < pad; i += 1) cells.push(null);
        for (let i = 0; i < days; i += 1) cells.push(start.add(i, 'day').format('YYYY-MM-DD'));
        while (cells.length % 7 !== 0) cells.push(null);

        const out: { cells: (string | null)[]; weekStart: string; allFuture: boolean }[] = [];
        for (let i = 0; i < cells.length; i += 7) {
            const chunk = cells.slice(i, i + 7);
            const real = chunk.filter(Boolean) as string[];
            out.push({
                cells: chunk,
                weekStart: real[0] ? dayjs(real[0]).startOf('week').format('YYYY-MM-DD') : '',
                // 통째로 미래인 주는 접는다. 빈 칸 다섯 줄이 화면을 잡아먹는다.
                allFuture: real.length > 0 && real.every((d) => dayjs(d).isAfter(today)),
            });
        }
        return out;
    }, [cursor, today]);

    /** 접힌 미래 주에 몇 밤 남았는지 */
    const futureRows = rows.filter((r) => r.allFuture);
    const firstFutureDate = futureRows.length
        ? (futureRows[0].cells.filter(Boolean) as string[])[0]
        : null;

    const selected = selectedDate ? byDate[selectedDate] : undefined;
    /*
     * 🔴 「이날 무엇이 브리핑에 오르지 못했나」 문구를 **화면에서 뺐다**
     *    (2026-09-01 QA33, 대표 지시).
     *
     * 브리핑 블록 안 노란 문구와 히트맵 위 노란 배너, 둘 다 뺐다.
     * 지난 리포트를 여는 이유는 「무엇을 처리했나」인데, 노란 글씨 두 덩이가
     * 그 위에서 매번 다른 이야기를 하고 있었다.
     *
     * ⚠️ **셈은 그대로다.** `explainComposition`·`leftOutOf`·`summarizeComposition`
     *    전부 살아 있고 히트맵의 「모서리 접힌 날」 표시도 그대로다 — 밀린 날이
     *    어느 날이었는지는 달력에서 여전히 보인다.
     */

    /**
     * 고른 날의 항목 — **오늘의 브리핑과 같은 편성**으로 다섯 건만 세운다.
     *
     * 예전에는 그날 올라온 것을 서른 줄 전부 늘어놨다. 오늘의 브리핑은 이미
     * 다섯 건으로 접혀 있는데 지난 리포트만 전부 펼쳐 두면, 같은 날을 두 화면에서
     * 보고 "어느 쪽이 그날 실제였나"를 알 수 없다.
     *
     * ⚠️ 슬롯이 들고 다니는 `index` 는 **서버 highlights 배열의 위치**다.
     *    완료 기록 API(`PATCH .../history/:date/items/:index`)가 그 위치로 항목을
     *    찾으므로, 화면에 보이는 순서를 index 로 쓰면 엉뚱한 항목이 완료된다.
     */
    const selectedItems = useMemo(
        () => composeHighlights(selected?.highlights ?? [], BRIEFING_FOCUS_COUNT),
        [selected]
    );
    const selectedRestCount = selectedItems.rest.length;
    /*
     * 「더 보기」가 없어져서(2026-08-28 대표 결정 — 지난 리포트는 그날의 다섯 건만)
     * 펼침 상태와 날짜마다 접는 초기화도 함께 사라졌다.
     */
    const lift = effect ? Math.round((effect.retainedWithAction - effect.retainedWithoutAction) * 10) / 10 : 0;

    /**
     * 조치 결과 추적이 아직 없는 기간은 금액이 null 로 옵니다.
     * 0원으로 찍으면 "한 푼도 못 지켰다"로 읽힙니다. 아직 안 셌다고 씁니다.
     */
    const moneyText = (v: number | null) => (v == null ? '집계 전' : `${won(v)}원`);

    const verdictTone =
        cmp.verdict === 'BETTER'
            ? { color: '#00795F', bg: '#EAF7F2', border: '#B7DFD2', icon: '📈' }
            : cmp.verdict === 'WORSE'
              ? { color: '#C22D38', bg: '#FFF0F0', border: '#FBD0D3', icon: '📉' }
              : cmp.verdict === 'SAME'
                ? { color: '#2C4C8C', bg: '#F1F5FC', border: '#D3E0F5', icon: '➖' }
                : { color: '#7C8794', bg: '#F1F4F9', border: '#E3EAF3', icon: '🗓' };

    const canPrev = dayjs(`${monthKey}-01`).subtract(1, 'month').isAfter(today.subtract(13, 'month'));
    const canNext = cursor.add(1, 'month').isBefore(today.add(1, 'day'));

    /**
     * 이번 달 — 서버가 본 오늘 기준입니다.
     * 기기 시계로 정하면 시간대가 KST 가 아닌 기기에서 '이번 달로' 가
     * 엉뚱한 달로 갑니다 (today 주석 참고).
     */
    const thisMonthKey = today.format('YYYY-MM');

    const goMonth = (next: string) => {
        userMovedMonth.current = true;
        /*
         * 고른 날짜를 그대로 두면 다른 달에서는 byDate 에 없는 키가 되어
         * 열려 있던 상세가 아무 말 없이 닫힙니다. 달을 옮길 때 같이 놓습니다.
         */
        setSelectedDate(null);
        setMonthKey(next);
    };

    const moveMonth = (delta: number) => {
        if (delta < 0 && !canPrev) return;
        if (delta > 0 && !canNext) return;
        goMonth(cursor.add(delta, 'month').format('YYYY-MM'));
    };

    const monthNav = (
        <MonthNav
            cursor={cursor}
            canPrev={canPrev}
            canNext={canNext}
            canReturnToThisMonth={monthKey !== thisMonthKey}
            onMove={moveMonth}
            onReturnToThisMonth={() => goMonth(thisMonthKey)}
        />
    );

    /**
     * 로딩·에러·빈 달에도 달 이동을 남기는 껍데기.
     * 이 셸이 없으면 기록 없는 달에 갇힙니다 (MonthNav 주석 참고).
     */
    const withMonthNav = (body: React.ReactNode) => (
        <div className="flex w-full flex-col pb-[40px]" style={{ gap: 14 }}>
            <div
                className="flex w-full items-center justify-between rounded-[16px]"
                style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, padding: '14px 18px' }}
            >
                <span style={{ fontSize: 16, fontWeight: 700, color: '#16202E' }}>지난 리포트</span>
                {monthNav}
            </div>
            {body}
        </div>
    );

    /* ── 상태 분기 ─────────────────────────────────────────── */

    if (!ctx.ready) {
        return (
            <OpsError
                title="센터 정보를 확인할 수 없습니다"
                detail="로그인 정보가 만료되었을 수 있습니다"
            />
        );
    }
    if (isLoading) return withMonthNav(<OpsLoading variant="page" />);
    if (isError) return withMonthNav(<OpsError onRetry={() => refetch()} />);
    /*
     * 빈 화면 판정은 '기록이 있느냐'로만 합니다.
     * 효과 검증(effect)·월간 총평(insights)은 조치 결과를 60일 추적해야
     * 나오는 값이라 한동안 null 입니다. 그걸 기준으로 막으면
     * 이미 쌓인 히트맵까지 안 보입니다.
     *
     * ⚠️ length 로 보면 안 됩니다. 브리핑이 안 만들어진 날도 items: 0 인 행으로
     * 채워 보내는 경로가 있어, length 는 30이지만 실제 기록은 0건인 달에도
     * "실행률 0%"를 띄우게 됩니다.
     *
     * ⚠️ 그렇다고 **올라온 항목이 있는 날**만으로 봐도 안 됩니다. 그러면
     *   · 한 달 내내 브리핑이 만들어지지 않은 달
     *   · 챙길 일이 0건이던 날만 있던 달 (= 평화로운 달)
     * 이 둘이 똑같이 "아직 지난 기록이 없습니다" 로 접힙니다. 앞은 기능이 안 돈
     * 것이고 뒤는 잘 굴러간 것인데, 화면이 둘을 같은 말로 덮습니다.
     * 그래서 기록 상태까지 본 worthShowing 으로 판정합니다.
     * 자동 후퇴(useEffect)도 같은 값을 쓰므로 판정이 어긋나면 안 됩니다.
     */
    if (!recordCoverage.worthShowing) {
        /*
         * 어느 달이 비었는지 밝힙니다.
         *
         * 단, **사용자가 직접 고른 달일 때만** 그렇게 말합니다.
         * 처음 들어와서 (자동 후퇴를 포함해) 여기 도달한 것은 "라포에 아직
         * 기록이 없다"는 뜻이라 원래 문구가 맞습니다. 반대로 `‹` 로 7월을
         * 골라서 왔다면 "아직 지난 기록이 없습니다"는 거짓말입니다 —
         * 8월에는 멀쩡히 기록이 있으니까요.
         */
        const userPicked = userMovedMonth.current;
        return withMonthNav(
            <OpsEmpty
                icon="🗓"
                title={
                    userPicked
                        ? `${cursor.format('YYYY년 M월')}에는 기록이 없습니다`
                        : '아직 지난 기록이 없습니다'
                }
                body={
                    userPicked
                        ? '이 달에는 브리핑이 만들어진 날이 없습니다. 위에서 다른 달을 골라 보세요.'
                        : '브리핑을 며칠 받아보시면 여기에 실행 기록이 쌓입니다.'
                }
                hint={
                    userPicked
                        ? undefined
                        : '한 달쯤 쌓이면 요일별 패턴과 지난달 대비 흐름까지 보여드릴 수 있습니다.'
                }
            />
        );
    }

    return (
        <div className="flex w-full flex-col pb-[40px]" style={{ gap: 14 }}>
            {isOpsBriefingMock() && <OpsMockBadge />}
            {/*
              화면이 스스로 달을 바꿨으면 그 사실을 밝힌다.
              조용히 바꾸면 8월 1일에 들어온 원장이 7월 숫자를 이번 달로 읽는다.
            */}
            {steppedBackFrom && steppedBackFrom !== monthKey && (
                <div
                    className="flex w-full items-center rounded-[10px]"
                    style={{ background: '#F1F5FC', border: '1px solid #D3E0F5', padding: '10px 14px', gap: 8 }}
                >
                    <span style={{ fontSize: 13 }}>🗓</span>
                    <span style={{ fontSize: 12.5, color: '#2C4C8C' }}>
                        {dayjs(`${steppedBackFrom}-01`).format('M월')}에는 아직 기록이 없어{' '}
                        <b>{cursor.format('M월')}</b> 기록을 보여드리고 있습니다. 아래 숫자는{' '}
                        {cursor.format('M월')} 기준입니다.
                    </span>
                </div>
            )}
            {/* ① 이번 달 성적 */}
            <div
                className="w-full overflow-hidden rounded-[16px]"
                style={{
                    background: 'linear-gradient(135deg, #1A305D 0%, #24406F 55%, #2B5183 100%)',
                    boxShadow: '0 10px 30px rgba(26,48,93,0.18)',
                }}
            >
                <div className="flex items-center justify-between" style={{ padding: '24px 30px' }}>
                    <div className="flex flex-col">
                        <span style={{ fontSize: 13, color: '#9DB4D8' }}>
                            {cursor.format('YYYY년 M월')} 운영 성적
                        </span>
                        <span
                            style={{
                                fontSize: 28,
                                fontWeight: 800,
                                color: '#FFFFFF',
                                marginTop: 7,
                                letterSpacing: '-0.03em',
                            }}
                        >
                            {/*
                              올라온 항목이 하루도 없는 달에 "브리핑 0일 중 0일을 전부 챙기셨습니다"는
                              말이 안 됩니다. 그 달이 어떤 달이었는지를 그대로 씁니다 —
                              기록이 없던 달과 챙길 일이 없던 달은 다른 달입니다.
                            */}
                            {cmp.current.activeDays === 0 ? (
                                recordCoverage.recordedDays === 0 && recordCoverage.notCreatedDays > 0 ? (
                                    '이 달은 브리핑이 만들어진 날이 없습니다'
                                ) : (
                                    '이 달은 챙길 일이 올라온 날이 없었습니다'
                                )
                            ) : (
                                <>
                                    브리핑 {cmp.current.activeDays}일 중{' '}
                                    <span style={{ color: '#8FE3CD' }}>{cmp.current.perfectDays}일</span>을 전부
                                    챙기셨습니다
                                </>
                            )}
                        </span>
                        <div className="flex items-center" style={{ gap: 8, marginTop: 10 }}>
                            <span
                                className="rounded-[7px]"
                                style={{
                                    background: 'rgba(255,255,255,0.14)',
                                    color: '#FFFFFF',
                                    fontSize: 13.5,
                                    fontWeight: 700,
                                    padding: '5px 11px',
                                }}
                            >
                                {verdictTone.icon} {cmp.headline}
                            </span>
                            {cmp.current.missedDays > 0 && (
                                <span
                                    className="rounded-[7px]"
                                    style={{
                                        background: 'rgba(255,62,74,0.2)',
                                        color: '#FFB0B6',
                                        fontSize: 13.5,
                                        fontWeight: 700,
                                        padding: '5px 11px',
                                    }}
                                >
                                    한 건도 못 챙긴 날 {cmp.current.missedDays}일
                                </span>
                            )}
                        </div>
                    </div>

                    {/* 연속 기록 — 습관은 연속으로 만들어진다 */}
                    <div className="flex items-center" style={{ gap: 0 }}>
                        {[
                            {
                                label: '🔥 연속 전부 실행',
                                v: `${streak.current}일`,
                                sub: `최고 기록 ${streak.best}일`,
                                tone: streak.current > 0 ? '#FFD877' : '#9DB4D8',
                            },
                            {
                                label: '✅ 이번 달 실행률',
                                /*
                                 * 올라온 항목이 0건이면 실행률이 존재하지 않는다.
                                 * 예전에는 rate 가 0 으로 내려와 "0%"가 찍혔는데,
                                 * 그건 원장이 다 놓쳤다는 뜻으로 읽힌다. 실제로는
                                 * 챙길 일 자체가 없었던 달이다.
                                 */
                                v: cmp.current.rate == null ? '이슈 없음' : `${pct(cmp.current.rate)}%`,
                                /*
                                 * 분모가 무엇인지 여기서 밝힌다.
                                 * 브리핑이 만들어지지 않은 날은 애초에 항목이 없어 분모에
                                 * 들어가지 않는데, 그 사실을 안 적으면 "그 달은 원래 이만큼이었구나"로
                                 * 읽힌다. 숫자를 보는 자리에 적어야 읽힌다.
                                 */
                                sub:
                                    cmp.current.rate == null
                                        ? `올라온 항목이 없었습니다${recordCoverage.denomTail}`
                                        : `${cmp.current.totalItems}건 중 ${cmp.current.totalActed}건${recordCoverage.denomTail}`,
                                tone: '#FFFFFF',
                            },
                        ].map((x, i) => (
                            <div
                                key={x.label}
                                className="flex flex-col"
                                style={{
                                    padding: '0 26px',
                                    borderLeft: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.14)',
                                    alignItems: 'flex-end',
                                }}
                            >
                                <span style={{ fontSize: 12.5, color: '#9DB4D8' }}>{x.label}</span>
                                <span
                                    style={{
                                        fontSize: 30,
                                        fontWeight: 800,
                                        color: x.tone,
                                        letterSpacing: '-0.035em',
                                        marginTop: 3,
                                    }}
                                >
                                    {x.v}
                                </span>
                                <span style={{ fontSize: 12, color: '#8FA5C4' }}>{x.sub}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* 금액 */}
                <div
                    className="flex items-center"
                    style={{
                        padding: '13px 30px',
                        borderTop: '1px solid rgba(255,255,255,0.12)',
                        background: 'rgba(0,0,0,0.12)',
                        gap: 22,
                    }}
                >
                    <span style={{ fontSize: 13.5, color: '#B7C7E0' }}>
                        🛡 지켜낸 매출{' '}
                        <b style={{ color: '#8FE3CD', fontSize: 16 }}>{moneyText(cmp.current.defended)}</b>
                    </span>
                    <span style={{ fontSize: 13.5, color: '#B7C7E0' }}>
                        💸 놓친 수익{' '}
                        <b style={{ color: '#FFB0B6', fontSize: 16 }}>{moneyText(cmp.current.lost)}</b>
                    </span>
                    <span style={{ marginLeft: 'auto', fontSize: 12.5, color: '#8FA5C4' }}>
                        {cmp.current.defended == null
                            ? '조치 이후 회원이 실제로 남았는지를 60일간 추적한 뒤에 집계됩니다'
                            : '지켜낸 매출은 조치 이후 실제로 재등록·수업이 이어진 건만 집계합니다'}
                    </span>
                </div>
            </div>

            {/* 실행 효과 검증 — 아직 추적 전이면 숫자를 만들지 않고 그렇다고 쓴다 */}
            {!effect ? (
                <div
                    className="flex items-center rounded-[14px]"
                    style={{
                        background: '#F7F9FC',
                        border: `1px dashed #C9D3E0`,
                        padding: '18px 24px',
                        gap: 14,
                    }}
                >
                    <span style={{ fontSize: 22 }}>⏳</span>
                    <div className="flex flex-col">
                        <span style={{ fontSize: 15.5, fontWeight: 700, color: '#425063' }}>
                            조치 효과는 아직 집계 전입니다
                        </span>
                        <span style={{ fontSize: 13.5, color: '#7C8794', marginTop: 4, lineHeight: '20px' }}>
                            &quot;챙긴 회원이 실제로 남았는가&quot;는 조치 후 60일을 지켜봐야 답이 나옵니다.
                            그 전에 숫자를 보여드리면 근거 없는 수치가 됩니다.
                        </span>
                    </div>
                </div>
            ) : (
            <div
                className="flex items-center rounded-[14px]"
                style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, padding: '18px 24px', gap: 26 }}
            >
                <div className="flex flex-col" style={{ flex: 'none' }}>
                    <span style={{ fontSize: 13, color: '#93A0AE' }}>조치했을 때 vs 안 했을 때</span>
                    <span style={{ fontSize: 16.5, fontWeight: 800, color: '#16202E', marginTop: 4 }}>
                        60일 뒤 회원이 남아 있는 비율
                    </span>
                </div>
                <div className="flex flex-1 flex-col" style={{ gap: 9 }}>
                    {[
                        { l: '조치함', v: effect.retainedWithAction, c: '#00A793' },
                        { l: '조치 안 함', v: effect.retainedWithoutAction, c: '#FF6B73' },
                    ].map((b) => (
                        <div key={b.l} className="flex items-center" style={{ gap: 11 }}>
                            <span style={{ fontSize: 13, color: '#7C8794', width: 74, flex: 'none' }}>{b.l}</span>
                            <div className="rounded-full" style={{ flex: 1, height: 14, background: '#EDF1F7' }}>
                                <div className="rounded-full" style={{ width: `${b.v}%`, height: 14, background: b.c }} />
                            </div>
                            <span style={{ fontSize: 15, fontWeight: 800, color: '#16202E', width: 54, textAlign: 'right' }}>
                                {b.v}%
                            </span>
                        </div>
                    ))}
                </div>
                <div
                    className="flex flex-none flex-col items-center rounded-[11px]"
                    /*
                     * 부호를 '+' 로 박아 두고 색도 초록으로 고정돼 있었다.
                     * 조치한 쪽이 오히려 덜 남으면 화면에 초록색 '+-4.1%p' 가 찍힌다.
                     * 그때야말로 "지금 하는 조치가 듣지 않는다"는 가장 중요한 신호라,
                     * 좋은 소식처럼 칠하면 안 된다.
                     */
                    style={{ background: lift >= 0 ? '#EAF7F2' : '#FFF0F0', padding: '12px 20px' }}
                >
                    <span style={{ fontSize: 12, color: lift >= 0 ? '#5D9384' : '#B4787D' }}>차이</span>
                    <span
                        style={{
                            fontSize: 26,
                            fontWeight: 800,
                            color: lift >= 0 ? '#00795F' : '#C22D38',
                            letterSpacing: '-0.03em',
                        }}
                    >
                        {lift > 0 ? '+' : ''}
                        {lift}%p
                    </span>
                </div>
            </div>
            )}

            {/* ② 히트맵 — 이 화면의 주인공이라 전폭으로 쓴다 */}
            <div className="flex flex-col" style={{ gap: 12 }}>
                <div className="flex flex-col" style={{ gap: 12, minWidth: 0 }}>
                    {/* 히트맵 */}
                    <div
                        className="rounded-[16px]"
                        style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, padding: '24px 26px' }}
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-baseline" style={{ gap: 10 }}>
                                <span style={{ fontSize: 20, fontWeight: 800, color: '#16202E' }}>경영 히트맵</span>
                                <span style={{ fontSize: 13.5, color: '#93A0AE' }}>
                                    하루하루 얼마나 챙겼는지 · 날짜를 누르면 그날 브리핑
                                </span>
                            </div>
                            {/*
                              달 이동은 한 컴포넌트만 씁니다.
                              여기와 로딩·빈 화면이 서로 다른 버튼이면, 한쪽만 고쳐서
                              또 갇히는 화면이 생깁니다.
                            */}
                            {monthNav}
                        </div>

                        {/* 요일 머리 + 주 요약 자리 */}
                        <div className="flex" style={{ gap: 10, marginTop: 18 }}>
                            <div className="grid flex-1" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
                                {WEEKDAYS.map((d, i) => (
                                    <span
                                        key={d}
                                        style={{
                                            fontSize: 13.5,
                                            fontWeight: 700,
                                            color: i === 0 ? '#E06A72' : i === 6 ? '#5B8DE0' : '#7C8794',
                                            textAlign: 'center',
                                            paddingBottom: 4,
                                        }}
                                    >
                                        {d}
                                    </span>
                                ))}
                            </div>
                            <span style={{ width: 92, flex: 'none', fontSize: 13, fontWeight: 700, color: '#7C8794', textAlign: 'center', paddingBottom: 4 }}>
                                주 실행률
                            </span>
                        </div>

                        {/* 주 단위로 한 줄씩 */}
                        <div className="flex flex-col" style={{ gap: 8, marginTop: 4 }}>
                            {rows.map((row, ri) => {
                                const wk = weeks.find((w) => w.weekStart === row.weekStart);

                                // 통째로 미래인 주는 한 줄로 합쳐 안내만 남긴다
                                if (row.allFuture) {
                                    const isFirst = firstFutureDate
                                        ? (row.cells.filter(Boolean) as string[])[0] === firstFutureDate
                                        : false;
                                    if (!isFirst) return null;
                                    const restDays = futureRows.reduce(
                                        (n, r) => n + (r.cells.filter(Boolean) as string[]).length,
                                        0
                                    );
                                    return (
                                        <div
                                            key="future"
                                            className="flex items-center justify-center rounded-[9px]"
                                            style={{
                                                height: 52,
                                                border: '1px dashed #DDE5F0',
                                                background: '#FCFDFE',
                                                gap: 7,
                                            }}
                                        >
                                            <span style={{ fontSize: 13.5, color: '#A7B0BC' }}>
                                                {dayjs(firstFutureDate!).format('M월 D일')} ~{' '}
                                                {cursor.endOf('month').format('D일')} · 남은 {restDays}일은 아직 오지
                                                않았습니다
                                            </span>
                                        </div>
                                    );
                                }

                                return (
                                    <div key={ri} className="flex items-stretch" style={{ gap: 10 }}>
                                        <div className="grid flex-1" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 8 }}>
                                            {row.cells.map((date, ci) => {
                                                if (!date) return <div key={`e${ci}`} style={{ height: 104 }} />;
                                                const d = byDate[date];
                                                /*
                                                 * 기록 상태 — 문서가 있었나. 건수와는 다른 축이다.
                                                 * days[] 에 건수가 있으면 그게 사실이므로 REPORTED 가 이긴다.
                                                 */
                                                const kind = cellKindOf(stateByDate[date], Boolean(d));
                                                const lv = levelOf(d);
                                                const meta = LEVEL_META[lv];
                                                const cell = kind === 'REPORTED' ? null : CELL_META[kind];
                                                /*
                                                 * 미래 판정도 서버 기준이다.
                                                 * coverage 가 없을 때(UNKNOWN)만 기기 시계로 물러선다.
                                                 */
                                                const isFuture =
                                                    kind === 'FUTURE' ||
                                                    (kind === 'UNKNOWN' && dayjs(date).isAfter(today));
                                                const isSel = selectedDate === date;
                                                const isToday = date === today.format('YYYY-MM-DD');
                                                const has = !isFuture && d && d.items > 0;
                                                /*
                                                 * 그날 브리핑에 오르지 못한 후보.
                                                 * null 이면 "모른다"라서 아무 표시도 하지 않는다 —
                                                 * 0 건인 날과 섞으면 화면이 없는 사실을 말한다.
                                                 */
                                                const leftOut = dayLeftOut(d);
                                                const marked =
                                                    kind === 'REPORTED' && !!leftOut && leftOut.total > 0;
                                                const openable = Boolean(has) || marked;

                                                return (
                                                    <button
                                                        key={date}
                                                        type="button"
                                                        onClick={() => setSelectedDate(openable ? date : null)}
                                                        disabled={isFuture}
                                                        className="flex flex-col rounded-[11px]"
                                                        title={
                                                            cell
                                                                ? /*
                                                                   * 기록이 없는 날. **원인을 말하지 않는다** —
                                                                   * 실패인지 대상에서 빠진 기간인지 서버도 모른다.
                                                                   */
                                                                  cell.note
                                                                : `${
                                                                      has
                                                                          ? `${meta.label} · ${d.acted}/${d.items}건`
                                                                          : /* 0 건인 날은 결함이 아니라 성과다. 문서는 만들어졌다 */
                                                                            '챙길 일이 없던 날 · 브리핑은 만들어졌습니다'
                                                                  }${
                                                                      marked
                                                                          ? ` · 브리핑에 오르지 못한 후보 ${leftOut?.total ?? 0}건`
                                                                          : ''
                                                                  }`
                                                        }
                                                        style={{
                                                            height: 104,
                                                            background: cell ? cell.bg : meta.bg,
                                                            border: isSel
                                                                ? '3px solid #1A305D'
                                                                : isToday
                                                                  ? '3px solid #3D73DD'
                                                                  : cell
                                                                    ? cell.border
                                                                    : `1px solid ${meta.border}`,
                                                            padding: '9px 11px',
                                                            position: 'relative',
                                                            overflow: 'hidden',
                                                            cursor: openable ? 'pointer' : 'default',
                                                            textAlign: 'left',
                                                            alignItems: 'stretch',
                                                            opacity: isFuture ? 0.45 : 1,
                                                            boxShadow: isSel ? '0 4px 14px rgba(26,48,93,0.22)' : 'none',
                                                        }}
                                                    >
                                                        {/*
                                                          모서리 접힘 — 그날 브리핑에 오르지 못한 후보가 있었다는 표시.
                                                          숫자를 하나 더 얹으면 히트맵의 주인공(실행 건수)이 죽는다.
                                                          뜻은 아래 범례에, 무엇이 몇 건 빠졌는지는 날짜를 눌렀을 때 나온다.
                                                        */}
                                                        {marked && (
                                                            <span
                                                                aria-hidden
                                                                style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    right: 0,
                                                                    width: 0,
                                                                    height: 0,
                                                                    borderTop: `15px solid ${meta.text}`,
                                                                    borderLeft: '15px solid transparent',
                                                                    opacity: 0.45,
                                                                }}
                                                            />
                                                        )}
                                                        <div className="flex items-center" style={{ gap: 5 }}>
                                                            <span
                                                                style={{
                                                                    fontSize: 17,
                                                                    fontWeight: 800,
                                                                    letterSpacing: '-0.02em',
                                                                    color: cell ? cell.text : meta.text,
                                                                }}
                                                            >
                                                                {dayjs(date).date()}
                                                            </span>
                                                            {isToday && (
                                                                <span
                                                                    className="rounded-[4px]"
                                                                    style={{
                                                                        background: '#3D73DD',
                                                                        color: '#FFFFFF',
                                                                        fontSize: 9.5,
                                                                        fontWeight: 800,
                                                                        padding: '2px 5px',
                                                                    }}
                                                                >
                                                                    오늘
                                                                </span>
                                                            )}
                                                        </div>

                                                        {has && (
                                                            <>
                                                                {/* 실행 도트 — 숫자보다 개수가 빨리 읽힌다 */}
                                                                <div className="flex flex-wrap" style={{ gap: 3.5, marginTop: 8 }}>
                                                                    {Array.from({ length: d.items }).map((_, k) => (
                                                                        <span
                                                                            key={k}
                                                                            style={{
                                                                                width: 8,
                                                                                height: 8,
                                                                                borderRadius: 4,
                                                                                background: k < d.acted ? meta.text : meta.muted,
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </div>

                                                                <div
                                                                    className="flex items-baseline"
                                                                    style={{ marginTop: 'auto', gap: 2 }}
                                                                >
                                                                    <span
                                                                        style={{
                                                                            fontSize: 19,
                                                                            fontWeight: 800,
                                                                            color: meta.text,
                                                                            letterSpacing: '-0.03em',
                                                                            lineHeight: '22px',
                                                                        }}
                                                                    >
                                                                        {d.acted}
                                                                    </span>
                                                                    <span
                                                                        style={{ fontSize: 13, fontWeight: 700, color: meta.text, opacity: 0.75 }}
                                                                    >
                                                                        /{d.items}건
                                                                    </span>
                                                                </div>
                                                                {(d.lost ?? 0) > 0 && (
                                                                    <span
                                                                        style={{ fontSize: 11, fontWeight: 700, color: meta.text, opacity: 0.8 }}
                                                                    >
                                                                        -{shortWon(d.lost as number)}
                                                                    </span>
                                                                )}
                                                            </>
                                                        )}
                                                        {/*
                                                          문서는 있었고 항목이 0 건이던 날.
                                                          이건 좋은 날이다 — 경고처럼 보이면 안 되고,
                                                          '브리핑이 없던 날'과 같은 칸으로 보여도 안 된다.
                                                        */}
                                                        {kind === 'REPORTED' && !has && (
                                                            <span
                                                                style={{ fontSize: 12, color: meta.text, marginTop: 'auto' }}
                                                            >
                                                                이슈 없음
                                                            </span>
                                                        )}
                                                        {/*
                                                          기록이 없는 날. 조용해야 한다 —
                                                          기능을 막 켠 센터는 그 달 대부분이 '기록 시작 전'이라
                                                          여기가 시끄러우면 달력이 통째로 경고판이 된다.
                                                        */}
                                                        {cell && cell.label && (
                                                            <span
                                                                style={{
                                                                    fontSize: 11.5,
                                                                    color: cell.text,
                                                                    marginTop: 'auto',
                                                                }}
                                                            >
                                                                {cell.label}
                                                            </span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {/* 주 요약 — 한 주가 어땠는지가 옆에 붙어야 흐름이 보인다 */}
                                        <div
                                            className="flex flex-none flex-col items-center justify-center rounded-[9px]"
                                            style={{
                                                width: 92,
                                                background: wk && wk.items > 0 ? '#F7F9FC' : 'transparent',
                                                border: wk && wk.items > 0 ? `1px solid ${BORDER}` : '1px solid transparent',
                                            }}
                                        >
                                            {wk && wk.items > 0 ? (
                                                <>
                                                    <span
                                                        style={{
                                                            fontSize: 22,
                                                            fontWeight: 800,
                                                            color:
                                                                wk.rate >= 0.8
                                                                    ? '#00795F'
                                                                    : wk.rate >= 0.5
                                                                      ? '#B45309'
                                                                      : '#C22D38',
                                                            letterSpacing: '-0.03em',
                                                        }}
                                                    >
                                                        {pct(wk.rate)}%
                                                    </span>
                                                    <span style={{ fontSize: 11.5, color: '#93A0AE', marginTop: 2 }}>
                                                        {wk.acted}/{wk.items}건
                                                    </span>
                                                </>
                                            ) : (
                                                <span style={{ fontSize: 11, color: '#D3DAE4' }}>—</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* 범례 */}
                        <div className="flex flex-wrap items-center" style={{ gap: 12, marginTop: 14 }}>
                            {(['PERFECT', 'GOOD', 'HALF', 'POOR', 'NONE'] as const).map((lv) => (
                                <span key={lv} className="flex items-center" style={{ gap: 5 }}>
                                    <span
                                        style={{
                                            width: 17,
                                            height: 17,
                                            borderRadius: 5,
                                            background: LEVEL_META[lv].bg,
                                            border: `1px solid ${LEVEL_META[lv].border}`,
                                        }}
                                    />
                                    <span style={{ fontSize: 13, color: '#5B6B7F' }}>{LEVEL_META[lv].label}</span>
                                </span>
                            ))}
                            <span className="flex items-center" style={{ gap: 5 }}>
                                <span style={{ width: 17, height: 17, borderRadius: 5, border: '1px dashed #C4CCD8' }} />
                                <span style={{ fontSize: 13, color: '#5B6B7F' }}>아직 오지 않은 날</span>
                            </span>
                            {/*
                              기록 상태 범례 — **그 달에 실제로 있는 것만** 올린다.
                              색만 늘리고 설명이 없으면 원장님이 못 읽고, 안 쓰는 색까지
                              나열하면 범례가 길어져서 아무도 안 읽는다.
                            */}
                            {legendKindsOf(recordCoverage).map((k) => (
                                <span key={k} className="flex items-center" style={{ gap: 5 }}>
                                    <span
                                        style={{
                                            width: 17,
                                            height: 17,
                                            borderRadius: 5,
                                            background: CELL_META[k].bg,
                                            border: CELL_META[k].border,
                                        }}
                                    />
                                    <span style={{ fontSize: 13, color: '#5B6B7F' }}>{CELL_META[k].legend}</span>
                                </span>
                            ))}
                            {/* 접힌 모서리의 뜻 — 표시가 실제로 있는 달에만 범례를 놓는다 */}
                            {compCoverage.leftOutDays > 0 && (
                                <span className="flex items-center" style={{ gap: 5 }}>
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            position: 'relative',
                                            width: 17,
                                            height: 17,
                                            borderRadius: 5,
                                            border: `1px solid ${BORDER}`,
                                            background: '#FFFFFF',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <span
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                right: 0,
                                                width: 0,
                                                height: 0,
                                                borderTop: '9px solid #5B6B7F',
                                                borderLeft: '9px solid transparent',
                                                opacity: 0.55,
                                            }}
                                        />
                                    </span>
                                    <span style={{ fontSize: 13, color: '#5B6B7F' }}>
                                        브리핑에 오르지 못한 후보가 있던 날
                                    </span>
                                </span>
                            )}
                        </div>

                        {/*
                          이 달 편성 기록.

                          올라온 것만 보여 주면 "이 달은 재등록 기회가 없었구나"로 읽힌다.
                          실제로는 후보가 있었는데 위험에 밀려 자리를 못 얻은 날이 있고,
                          그 차이를 모르면 기회를 통째로 놓친다.

                          ⚠️ 편성 기록이 없는 날(구버전 문서)은 "모른다"다. 0 건인 날과 섞지 않는다 —
                          문구 자체가 그 둘을 다르게 말하도록 summarizeComposition 에서 갈라 둔다.
                        */}
                        {/*
                          기록 상태 한 줄.

                          히트맵의 칸 하나하나는 그날을 말하지만, "이 달 숫자가 며칠을 센
                          것인가"는 칸을 다 세어 봐야 안다. 그건 아무도 안 한다.
                          그래서 분모를 여기서 한 줄로 밝힌다.

                          ⚠️ 경고 색을 쓰지 않는다. 브리핑이 없던 날은 원장님 잘못이 아니고,
                          원인도 우리가 모른다. 사실만 조용히 적는다.
                        */}
                        {recordCoverage.note && (
                            <div
                                className="flex rounded-[10px]"
                                style={{
                                    marginTop: 12,
                                    padding: '11px 14px',
                                    background: '#F7F9FC',
                                    borderLeft: '3px solid #C4CCD8',
                                }}
                            >
                                <span style={{ fontSize: 13.5, color: '#5B6B7F', lineHeight: '20px' }}>
                                    {recordCoverage.note}
                                </span>
                            </div>
                        )}

                        {/*
                          🔴 밀린 후보를 알리던 **노란 배너를 뺐다** (2026-09-01 QA33).

                          「재등록 기회 후보 7건이 자리가 없어 8월 브리핑에 오르지
                          못했습니다 … 실행률에는 들어 있지 않습니다」가 히트맵 위에
                          늘 떠 있었다. 대표 지시로 화면에서 뺀다.

                          ⚠️ **회색 안내는 남긴다.** 「편성 기록이 남아 있지 않아 알 수
                             없습니다」는 밀린 이야기가 아니라 **분모 이야기**다. 이걸
                             같이 지우면 기록이 없는 달이 깨끗한 달로 보인다 —
                             바로 위 `recordCoverage.note` 를 남겨 두는 것과 같은 이유다.
                             `alert` 은 `leftOutDays > 0` 이라, 노란 경우와 정확히 같다.
                        */}
                        {compCoverage.text && !compCoverage.alert && (
                            <div
                                className="flex flex-col rounded-[10px]"
                                style={{
                                    marginTop: 12,
                                    padding: '11px 14px',
                                    gap: 4,
                                    background: '#F7F9FC',
                                    borderLeft: '3px solid #C4CCD8',
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: 13.5,
                                        color: '#5B6B7F',
                                        lineHeight: '20px',
                                    }}
                                >
                                    {compCoverage.text}
                                </span>
                            </div>
                        )}

                        {/* 선택한 날 */}
                        {selected && (
                            <div className="rounded-[12px]" style={{ marginTop: 16, border: `1px solid ${BORDER}`, overflow: 'hidden' }}>
                                <div className="flex items-center justify-between" style={{ background: '#F7F9FC', padding: '13px 16px' }}>
                                    <div className="flex items-baseline" style={{ gap: 9 }}>
                                        <span style={{ fontSize: 15.5, fontWeight: 700, color: '#16202E' }}>
                                            {dayjs(selected.date).format('M월 D일 (ddd)')} 브리핑
                                        </span>
                                        {/*
                                          접었다는 사실은 밝힌다. 안 밝히면 "이날은 다섯 건뿐이었구나"
                                          로 읽힌다 — 오늘의 브리핑 머리말과 같은 이유다.
                                        */}
                                        {selectedRestCount > 0 && (
                                            <span style={{ fontSize: 12.5, color: '#5B6B7F' }}>
                                                급한 순으로 {selectedItems.focus.length}건 ·
                                                {' '}이날 올라온 나머지 {selectedRestCount}건은 제외
                                            </span>
                                        )}
                                        <span style={{ fontSize: 12.5, color: '#93A0AE' }}>
                                            지난 건도 지금 처리하고 체크할 수 있습니다
                                        </span>
                                    </div>
                                    {/* 좁은 폭에서 배지가 밀리면 줄을 나눈다 — 금액이 쪼개지는 것보다 낫다 */}
                                    <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
                                        <span style={{ fontSize: 13.5, color: '#5B6B7F' }}>
                                            {selected.items}건 중{' '}
                                            <b style={{ color: '#00795F' }}>{selected.acted}건 실행</b>
                                            {selected.dismissed > 0 && ` · ${selected.dismissed}건 넘김`}
                                        </span>
                                        {(selected.defended ?? 0) > 0 && (
                                            <span
                                                className="rounded-[6px]"
                                                style={{ background: '#EAF7F2', color: '#00795F', fontSize: 12.5, fontWeight: 700, padding: '3px 9px', whiteSpace: 'nowrap', flex: 'none' }}
                                            >
                                                +{won(selected.defended as number)}원 방어
                                            </span>
                                        )}
                                        {(selected.lost ?? 0) > 0 && (
                                            <span
                                                className="rounded-[6px]"
                                                style={{ background: '#FFF0F0', color: '#C22D38', fontSize: 12.5, fontWeight: 700, padding: '3px 9px', whiteSpace: 'nowrap', flex: 'none' }}
                                            >
                                                -{won(selected.lost as number)}원 손실
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {/*
                                  🔴 이날 밀린 후보를 적던 **노란 문구를 뺐다** (QA33).
                                     히트맵 위 배너와 같은 이유다 — 지난 리포트를 여는
                                     이유는 「무엇을 처리했나」인데 노란 글씨가 그 위에서
                                     다른 이야기를 하고 있었다. 셈(`explainComposition`)은
                                     그대로 살아 있다.
                                */}
                                {/*
                                  편성 기록이 없는 날은 **모르는 날**이다.
                                  아무 말도 안 하면 "빠진 게 없었구나"로 읽히므로 모른다고 적는다.
                                */}
                                {selected.composition == null && (
                                    <div
                                        style={{
                                            padding: '10px 16px',
                                            background: '#F9FAFC',
                                            borderTop: `1px solid ${BORDER}`,
                                        }}
                                    >
                                        <span style={{ fontSize: 12.5, color: '#93A0AE', lineHeight: '19px' }}>
                                            이날은 편성 기록이 남아 있지 않아, 브리핑에 오르지 못한 항목이
                                            있었는지는 알 수 없습니다
                                        </span>
                                    </div>
                                )}
                                {selected.highlights.length === 0 ? (
                                    <div style={{ padding: '20px 16px', fontSize: 13.5, color: '#93A0AE' }}>
                                        이날 브리핑 {selected.items}건 중 {selected.acted}건을 실행하셨습니다. 세부
                                        항목 기록은 남아 있지 않습니다.
                                    </div>
                                ) : (
                                    /*
                                      🔴 **그날의 다섯 건만** 보여준다 (2026-08-28 대표 결정).
                                         「외 N건」은 오늘의 브리핑에서도 '오늘 할 일'이 아니라
                                         추가 브리핑이다. 지난 리포트는 「그날 무엇을 챙겼나」를
                                         돌아보는 화면이라, 그날 세우지도 않았던 건을 함께
                                         펼쳐 두면 그날의 실제 화면과 다른 기록이 된다.
                                    */
                                    selectedItems.focus.map(({ entry: h, index: i }, row) => {
                                        const done = h.done;
                                        return (
                                            <div
                                                /*
                                                  ⚠️ `i` 는 서버 배열 위치(완료 기록용)이고
                                                     `row` 는 화면에서 몇 번째 줄인가다. 구분선은
                                                     화면 순서를 봐야 한다 — 위험 순으로 다시
                                                     세우므로 첫 줄의 `i` 가 0 이 아닐 수 있다.
                                                */
                                                key={`${i}-${h.text}`}
                                                className="flex items-center"
                                                style={{
                                                    padding: '13px 16px',
                                                    borderTop: row === 0 ? 'none' : '1px solid #F1F4F9',
                                                    gap: 11,
                                                    background: done ? '#FFFFFF' : '#FFFCFC',
                                                }}
                                            >
                                                {/* 지난 건도 지금 처리할 수 있어야 한다 */}
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        /*
                                                         * 성공 토스트는 반드시 onSuccess 에서 띄운다.
                                                         * 예전에는 mutate 바로 다음 줄에서 동기로 띄웠다 —
                                                         * 서버가 거절해도 "실행 완료로 기록했습니다"가 먼저 뜨고,
                                                         * 그 뒤에 실패 토스트가 겹쳐 떴다. 원장은 앞의 문장을 믿는다.
                                                         *
                                                         * 화면 반응이 굼뜨지는 않는다 — 체크 표시 자체는
                                                         * 훅의 낙관적 업데이트가 즉시 바꾸고, 실패하면 되돌린다.
                                                         */
                                                        setItemDone.mutate(
                                                            { date: selected.date, index: i, done: !done },
                                                            {
                                                                onSuccess: () =>
                                                                    fire(
                                                                        done
                                                                            ? '실행 표시를 해제했습니다'
                                                                            : '실행 완료로 기록했습니다'
                                                                    ),
                                                                onError: () =>
                                                                    fire('기록에 실패했습니다. 잠시 후 다시 시도해 주세요'),
                                                            }
                                                        );
                                                    }}
                                                    title={done ? '눌러서 실행 해제' : '눌러서 실행 완료로 기록'}
                                                    aria-label={`${h.text} ${done ? '실행 해제' : '실행 완료'}`}
                                                    className="flex flex-none items-center justify-center"
                                                    style={{
                                                        width: 26,
                                                        height: 26,
                                                        borderRadius: 7,
                                                        padding: 0,
                                                        background: done ? '#00A793' : '#FFFFFF',
                                                        border: done ? '1px solid #00A793' : '2px solid #CBD5E3',
                                                        color: '#FFFFFF',
                                                        fontSize: 15,
                                                        fontWeight: 800,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    {done ? '✓' : ''}
                                                </button>

                                                <span
                                                    className="rounded-[5px]"
                                                    style={{
                                                        background: `${gradeColorOf(h.grade)}1A`,
                                                        color: gradeColorOf(h.grade),
                                                        fontSize: 11.5, fontWeight: 800, padding: '3px 8px', flex: 'none',
                                                    }}
                                                >
                                                    {gradeLabelOf(h.grade)}
                                                </span>
                                                {/*
                                                  제목만 있으면 "페이스 절반"을 보고도 얼마나 심각한지 몰라
                                                  다시 원본을 찾아 들어가야 한다. 판단에 쓴 수치를 옆에 붙인다.
                                                */}
                                                <div className="flex items-center" style={{ flex: 1, gap: 10, minWidth: 0 }}>
                                                    <span
                                                        style={{
                                                            fontSize: 14.5,
                                                            color: done ? '#7C8794' : '#16202E',
                                                            flex: 'none',
                                                            textDecoration: done ? 'line-through' : 'none',
                                                        }}
                                                    >
                                                        {h.text}
                                                    </span>
                                                    {h.coachName && (
                                                        <span style={{ fontSize: 12.5, color: '#93A0AE', flex: 'none' }}>
                                                            담당 {h.coachName}
                                                        </span>
                                                    )}
                                                    {h.facts && h.facts.length > 0 && (
                                                        <div
                                                            className="flex items-center"
                                                            style={{ gap: 5, flexWrap: 'wrap', minWidth: 0 }}
                                                        >
                                                            {h.facts.map((f) => (
                                                                <span
                                                                    key={f}
                                                                    className="rounded-[5px]"
                                                                    style={{
                                                                        background: done ? '#F4F6F9' : '#F1F5FC',
                                                                        color: done ? '#93A0AE' : '#3A4757',
                                                                        border: `1px solid ${done ? '#EDF1F7' : '#DCE6F7'}`,
                                                                        fontSize: 12,
                                                                        fontWeight: 600,
                                                                        padding: '2px 7px',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                >
                                                                    {f}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <span
                                                    style={{
                                                        fontSize: 13, fontWeight: 700,
                                                        color: done ? '#00795F' : '#C22D38', flex: 'none',
                                                    }}
                                                >
                                                    {done ? '실행함' : '미실행'}
                                                </span>
                                            </div>
                                        );
                                    })
                                )}

                                {/*
                                  🔴 「더 보기」를 뺐다 (2026-08-28 대표 결정).
                                     지난 리포트는 **그날의 다섯 건만** 보여준다.
                                     그날 세우지도 않았던 건을 펼칠 수 있게 두면
                                     그날의 실제 화면과 다른 기록이 된다.
                                     빠진 건수는 위 머리말이 밝힌다.
                                */}
                            </div>
                        )}
                    </div>

                    {/* ③ 요일 패턴 + ④ 주간 추세 */}
                    <div className="flex" style={{ gap: 12 }}>
                        <div
                            className="flex flex-1 flex-col rounded-[16px]"
                            style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, padding: '20px 22px', minWidth: 0 }}
                        >
                            <span style={{ fontSize: 17, fontWeight: 700, color: '#16202E' }}>요일별 실행 패턴</span>
                            <span style={{ fontSize: 13, color: '#93A0AE', marginTop: 4 }}>
                                최근 기록 전체 기준
                            </span>

                            <div className="flex" style={{ gap: 8, marginTop: 18, height: 168 }}>
                                {wdStats.map((w) => {
                                    const has = w.activeDays > 0;
                                    const worst = wdInsight.meaningful && wdInsight.worst?.weekday === w.weekday;
                                    const best = wdInsight.meaningful && wdInsight.best?.weekday === w.weekday;
                                    const tone = !has ? '#EDF1F7' : worst ? '#FF5A64' : best ? '#00A793' : '#B9CBE6';
                                    return (
                                        <div key={w.weekday} className="flex flex-1 flex-col" style={{ minWidth: 0 }}>
                                            <span
                                                style={{
                                                    fontSize: 13,
                                                    fontWeight: 700,
                                                    textAlign: 'center',
                                                    color: has ? (worst ? '#C22D38' : best ? '#00795F' : '#5B6B7F') : '#D3DAE4',
                                                    marginBottom: 6,
                                                }}
                                            >
                                                {has ? `${pct(w.rate)}%` : '—'}
                                            </span>
                                            {/* 막대가 자랄 자리 — flex:1 로 잡아야 비율 높이가 먹는다 */}
                                            <div className="flex flex-1 flex-col justify-end">
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        height: `${has ? Math.max(w.rate * 100, 3) : 3}%`,
                                                        minHeight: 4,
                                                        borderRadius: 5,
                                                        background: tone,
                                                    }}
                                                />
                                            </div>
                                            <span
                                                style={{
                                                    fontSize: 13,
                                                    textAlign: 'center',
                                                    color: worst ? '#C22D38' : best ? '#00795F' : '#7C8794',
                                                    fontWeight: worst || best ? 700 : 400,
                                                    marginTop: 8,
                                                }}
                                            >
                                                {w.label}
                                            </span>
                                            <span style={{ fontSize: 10.5, color: '#B4BDC9', textAlign: 'center', marginTop: 2 }}>
                                                {has ? `${w.activeDays}일` : ''}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            <div
                                className="rounded-[10px]"
                                style={{
                                    marginTop: 14,
                                    background: wdInsight.meaningful ? '#FFF7F7' : '#F7F9FC',
                                    borderLeft: `3px solid ${wdInsight.meaningful ? '#FF5A64' : '#C4CCD8'}`,
                                    padding: '12px 14px',
                                }}
                            >
                                <span style={{ fontSize: 14, color: '#2C3A4B', lineHeight: '21px' }}>
                                    {wdInsight.meaningful ? '🔍 ' : ''}
                                    {wdInsight.text}
                                </span>
                                {wdInsight.meaningful && wdInsight.worst && (
                                    <span style={{ fontSize: 13, color: '#7C8794', display: 'block', marginTop: 6, lineHeight: '19px' }}>
                                        {wdInsight.worst.label}요일 아침에 손실 시그널 알림을 한 번 더 받도록 설정해
                                        보세요. 요일 문제는 습관으로 고쳐집니다.
                                    </span>
                                )}
                            </div>
                        </div>

                        <div
                            className="flex flex-1 flex-col rounded-[16px]"
                            style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, padding: '20px 22px', minWidth: 0 }}
                        >
                            <span style={{ fontSize: 17, fontWeight: 700, color: '#16202E' }}>주간 실행률 흐름</span>
                            <span style={{ fontSize: 13, color: '#93A0AE', marginTop: 4 }}>
                                최근 {trend.length}주 · 오르는 중인지가 중요합니다
                            </span>

                            <div className="flex" style={{ gap: 6, marginTop: 18, height: 168 }}>
                                {trend.map((t, i) => {
                                    const prev = i > 0 ? trend[i - 1] : null;
                                    const up = prev ? t.rate >= prev.rate : true;
                                    const last = i === trend.length - 1;
                                    return (
                                        <div key={t.weekStart} className="flex flex-1 flex-col" style={{ minWidth: 0 }}>
                                            <span
                                                style={{
                                                    fontSize: 12.5,
                                                    fontWeight: last ? 800 : 600,
                                                    textAlign: 'center',
                                                    color: last ? '#16202E' : '#93A0AE',
                                                    marginBottom: 6,
                                                }}
                                            >
                                                {pct(t.rate)}%
                                            </span>
                                            <div className="flex flex-1 flex-col justify-end">
                                                <div
                                                    style={{
                                                        width: '100%',
                                                        height: `${Math.max(t.rate * 100, 3)}%`,
                                                        minHeight: 4,
                                                        borderRadius: 5,
                                                        background: last ? '#1A305D' : up ? '#8FD9C8' : '#F2A0A6',
                                                    }}
                                                />
                                            </div>
                                            <span
                                                style={{ fontSize: 11, color: '#A7B0BC', marginTop: 8, whiteSpace: 'nowrap', textAlign: 'center' }}
                                            >
                                                {t.label}
                                            </span>
                                            <span style={{ fontSize: 10.5, color: '#C4CCD8', textAlign: 'center', marginTop: 2 }}>
                                                {t.items}건
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {trend.length >= 2 && (
                                <div
                                    className="rounded-[10px]"
                                    style={{
                                        marginTop: 14,
                                        background:
                                            trend[trend.length - 1].rate >= trend[0].rate ? '#EAF7F2' : '#FFF7F7',
                                        borderLeft: `3px solid ${
                                            trend[trend.length - 1].rate >= trend[0].rate ? '#00A793' : '#FF5A64'
                                        }`,
                                        padding: '12px 14px',
                                    }}
                                >
                                    <span style={{ fontSize: 14, color: '#2C3A4B', lineHeight: '21px' }}>
                                        {trend[trend.length - 1].rate >= trend[0].rate
                                            ? `첫 주 ${pct(trend[0].rate)}% → 이번 주 ${pct(trend[trend.length - 1].rate)}%로 올라오고 있습니다.`
                                            : `첫 주 ${pct(trend[0].rate)}% → 이번 주 ${pct(trend[trend.length - 1].rate)}%로 떨어졌습니다. 무엇이 달라졌는지 확인해 보세요.`}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

            </div>

            {/* ⑤ 이달의 회고 — 히트맵 아래로 내려 가로로 편다.
                집계 근거가 없으면 빈 카드를 놓지 않고 아예 뺀다 */}
            {insights && (
            <div className="flex" style={{ gap: 12, alignItems: 'stretch' }}>
                <div
                    className="flex flex-1 flex-col rounded-[14px]"
                    style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, borderTop: '4px solid #FF3E4A', padding: '20px 22px', minWidth: 0 }}
                >
                    <span style={{ fontSize: 16, fontWeight: 800, color: '#16202E' }}>
                        🩸 {insights.critical.title}
                    </span>
                    <p style={{ fontSize: 14.5, color: '#3A4757', marginTop: 10, lineHeight: '22px' }}>
                        {insights.critical.body}
                    </p>
                    <div
                        className="rounded-[9px]"
                        style={{ background: '#FFF0F0', padding: '11px 14px', marginTop: 'auto', marginBlockStart: 14 }}
                    >
                        <span style={{ fontSize: 12.5, color: '#C22D38' }}>이 건으로 사라진 매출</span>
                        <div className="flex items-baseline" style={{ gap: 3, marginTop: 3 }}>
                            <span style={{ fontSize: 22, fontWeight: 800, color: '#C22D38' }}>
                                {won(insights.critical.amount)}
                            </span>
                            <span style={{ fontSize: 13, color: '#C22D38' }}>원</span>
                        </div>
                    </div>
                </div>

                <div
                    className="flex flex-1 flex-col rounded-[14px]"
                    style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, borderTop: '4px solid #00A793', padding: '20px 22px', minWidth: 0 }}
                >
                    <span style={{ fontSize: 16, fontWeight: 800, color: '#16202E' }}>
                        🏆 {insights.mvp.title}
                    </span>
                    <span
                        style={{ fontSize: 22, fontWeight: 800, color: '#00795F', marginTop: 10, display: 'block', letterSpacing: '-0.02em' }}
                    >
                        {insights.mvp.name}
                    </span>
                    <p style={{ fontSize: 14, color: '#5B6B7F', marginTop: 6, lineHeight: '21px' }}>
                        {insights.mvp.detail}
                    </p>
                    {/*
                      onClick 이 아예 없는데 cursor: pointer 만 달려 있었다.
                      누르면 반응할 것처럼 보이고 실제로는 아무 일도 안 일어난다.
                      강사에게 메시지를 보내는 서버 API 가 없으므로 정직하게 잠근다.
                    */}
                    <OpsComingSoonButton
                        label="감사 메시지 보내기"
                        reason="강사에게 메시지를 보내는 기능은 아직 제공 전입니다."
                        style={{ width: '100%', marginTop: 'auto', marginBlockStart: 14 }}
                    />
                </div>

                <div
                    className="flex flex-1 flex-col rounded-[14px]"
                    style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, borderTop: '4px solid #3D73DD', padding: '20px 22px', minWidth: 0 }}
                >
                    <span style={{ fontSize: 16, fontWeight: 800, color: '#16202E' }}>
                        🎯 {insights.nextMonth.title}
                    </span>
                    <p style={{ fontSize: 14.5, color: '#3A4757', marginTop: 10, lineHeight: '22px' }}>
                        {insights.nextMonth.body}
                    </p>
                    <div
                        className="rounded-[9px]"
                        style={{ background: '#F7F9FC', padding: '11px 14px', marginTop: 'auto', marginBlockStart: 14 }}
                    >
                        <span style={{ fontSize: 12.5, color: '#7C8794', lineHeight: '18px' }}>
                            {effect
                                ? `AI 예측 적중률 ${effect.predictionAccuracy}% · 틀렸다고 알려주신 건 ${effect.reportedWrong}건`
                                : '예측 적중률은 조치 결과가 쌓인 뒤에 집계됩니다'}
                        </span>
                    </div>
                </div>
            </div>
            )}

            <span style={{ fontSize: 12.5, color: '#A7B0BC', lineHeight: '19px' }}>
                브리핑 이슈가 없던 날은 실행률 계산에서 뺍니다. 챙길 게 없던 날을 못한 날로 세면 숫자가
                왜곡됩니다.
                {/*
                  실행률의 분모는 "브리핑에 올라온 항목"이다. 자리에 밀려 안 올라온 건은
                  지시가 나간 적이 없으므로 원장님 실행률을 깎을 수 없다 — 그건 편성이 정한 일이다.
                  다만 그 사실을 안 밝히면 100% 를 "그날 할 일이 없었다"로 읽게 된다.

                  ⚠️ **빠진 날이 실제로 있는 달에만** 적는다. 빠진 게 없는 달에도 매달 같은
                  각주가 떠 있으면, 정작 빠진 날이 있는 달에 이 문장이 안 읽힌다.
                  판정은 히트맵 위 안내와 같은 compCoverage 를 쓴다 — 두 자리가 다른 날을
                  세면 안 된다.
                */}
                {compCoverage.leftOutDays > 0 && (
                    <>
                        <br />
                        실행률은 브리핑에 올라온 항목만 셉니다. 편성에서 빠진 후보는 지시가 나가지
                        않았으므로 분모에 넣지 않습니다 — 실행률 100%가 &quot;그날 챙길 일이 하나도
                        남지 않았다&quot;는 뜻은 아닙니다.
                    </>
                )}
            </span>

            {toast && (
                <div
                    className="fixed left-1/2 rounded-[10px]"
                    style={{
                        bottom: 40, transform: 'translateX(-50%)', background: '#1A305D', color: '#FFFFFF',
                        fontSize: 15.5, padding: '14px 22px', zIndex: 1000, boxShadow: '0 8px 24px rgba(26,48,93,0.24)',
                    }}
                >
                    {toast}
                </div>
            )}
        </div>
    );
};

export default OpsBriefingHistoryPage;
